import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
  RemoteConfig
} from 'firebase/remote-config';
import { getFirebaseApp, isFirebaseEnabled } from './firebase';
import type { RemoteConfigProvider } from '@meiuca/remote-config';

export interface FirebaseRemoteConfigOptions {
  /** Intervalo mínimo entre fetches em milissegundos */
  minimumFetchIntervalMillis?: number;
  /** Timeout para fetch em milissegundos */
  fetchTimeoutMillis?: number;
  /** Valores padrão para configurações */
  defaultConfig?: Record<string, string | number | boolean>;
  /** Modo de desenvolvimento (usa intervalos menores) */
  isDevelopment?: boolean;
}

/**
 * Implementação de RemoteConfigProvider usando Firebase Remote Config
 *
 * Este provider implementa a interface RemoteConfigProvider de @meiuca/remote-config,
 * permitindo que o Remote Config Service use o Firebase como backend de configuração.
 *
 * Dependency Inversion: Firebase depende de remote-config (interface), não o contrário!
 */
export class FirebaseRemoteConfigProvider implements RemoteConfigProvider {
  private remoteConfig: RemoteConfig | null = null;
  private initialized = false;

  async initialize(options: FirebaseRemoteConfigOptions = {}): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (!isFirebaseEnabled()) {
      console.warn('[FIREBASE_RC_PROVIDER] Firebase not enabled, provider will return undefined values');
      this.initialized = true;
      return;
    }

    try {
      const app = getFirebaseApp();
      if (!app) {
        throw new Error('Firebase app not initialized');
      }

      this.remoteConfig = getRemoteConfig(app);

      // Configurações do Remote Config
      const isDev = options.isDevelopment ?? false;
      this.remoteConfig.settings = {
        minimumFetchIntervalMillis: options.minimumFetchIntervalMillis ?? (isDev ? 60000 : 3600000),
        fetchTimeoutMillis: options.fetchTimeoutMillis ?? 60000
      };

      // Define valores padrão se fornecidos
      if (options.defaultConfig) {
        this.remoteConfig.defaultConfig = options.defaultConfig;
      }

      // Busca e ativa as configurações remotas
      await fetchAndActivate(this.remoteConfig);
      console.log('[FIREBASE_RC_PROVIDER] Remote config fetched and activated');

      this.initialized = true;
    } catch (error) {
      console.error('[FIREBASE_RC_PROVIDER] Failed to initialize:', error);
      this.initialized = true; // Marca como inicializado mesmo com erro
    }
  }

  getNumber(key: string): number | undefined {
    if (!this.remoteConfig) {
      return undefined;
    }

    try {
      const value = getValue(this.remoteConfig, key);
      return value.asNumber();
    } catch (error) {
      console.error(`[FIREBASE_RC_PROVIDER] Error getting number ${key}:`, error);
      return undefined;
    }
  }

  getBoolean(key: string): boolean | undefined {
    if (!this.remoteConfig) {
      return undefined;
    }

    try {
      const value = getValue(this.remoteConfig, key);
      return value.asBoolean();
    } catch (error) {
      console.error(`[FIREBASE_RC_PROVIDER] Error getting boolean ${key}:`, error);
      return undefined;
    }
  }

  getString(key: string): string | undefined {
    if (!this.remoteConfig) {
      return undefined;
    }

    try {
      const value = getValue(this.remoteConfig, key);
      return value.asString();
    } catch (error) {
      console.error(`[FIREBASE_RC_PROVIDER] Error getting string ${key}:`, error);
      return undefined;
    }
  }

  async refresh(): Promise<void> {
    if (!this.remoteConfig) {
      return;
    }

    try {
      await fetchAndActivate(this.remoteConfig);
      console.log('[FIREBASE_RC_PROVIDER] Config refreshed');
    } catch (error) {
      console.error('[FIREBASE_RC_PROVIDER] Error refreshing config:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
